/**
 * @module utils/customColumnConfigDelete
 * @description 删除自定义字段定义时，同步清理图表设置（Context base/draft + save_column_infos）
 *
 * 与改名类似，入口在 DVA `deleteConditionByCustomColumn`，不能仅依赖 useQBLinkagePipeline：
 * - 管道有 debounce，删除后用户可能立即打开面板仍看到旧配置
 * - model 侧需与 save_column_infos 双写，避免持久化与 Context 割裂
 *
 * 具体 prune 规则复用 QB 联动 handler（runQBLinkagePipeline），与日常拖删字段一致。
 *
 * @see model.ts deleteConditionByCustomColumn
 * @see chartSettingsBridge.applyCustomColumnDeleteOnQB
 */

import { ConfigChartType, type TChartType } from '@dbl/core/chart';
import { detectDateDimensionsInList } from '../manifest/dimensionDateUtils';
import { getQBLinkageHandlers } from '../linkage/qbFields/qbLinkage/registry';
import { runQBLinkagePipeline } from '../linkage/qbFields/qbLinkage/runPipeline';
import type {
  IFieldSnapshot,
  IQBChangeSet,
  IQBLinkageContext,
  IQBSnapshot,
  TQBCategory,
  TQBEvent,
} from '../linkage/qbFields/qbLinkage/types';
import { snapshotList, toFieldSnapshot } from '../linkage/qbFields/qbLinkage/fieldSnapshot';
import { mergeConfigLayer } from './mergeConfigPatch';
import {
  pruneDatasetFieldSortLayerIfNeeded,
  shouldResetDatasetFieldSortOnCustomDelete,
  shouldResetDatasetFieldSortOnUnresolved,
} from '../settingGroups/common/SortByPanel/DatasetFieldSort/datasetFieldSortSync';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@dbl/pages/Multidimensional/Analysis/interface';

export interface ICollectRemovedSnapshotsInput {
  deleteCustomColumnId: string;
  columnDimensions: readonly unknown[];
  columnMetrics: readonly unknown[];
  columnX?: readonly unknown[];
  columnShowDimensions?: readonly unknown[];
}

export interface ICollectRemovedSnapshotsResult {
  removedDimensions: IFieldSnapshot[];
  removedMetrics: IFieldSnapshot[];
}

function pushRemovedFromList(
  list: readonly unknown[],
  deleteId: string,
  builderType: 'dimension' | 'metric',
  target: IFieldSnapshot[],
): void {
  for (const item of list) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as { id?: string };
    if (row.id !== deleteId) {
      continue;
    }
    const snap = toFieldSnapshot(item, builderType);
    if (snap) {
      target.push(snap);
    }
  }
}

/** 从删除前的 QB 列表收集将被移除的字段快照（用于构造联动 changeSet） */
export function collectRemovedSnapshotsForCustomColumn(
  input: ICollectRemovedSnapshotsInput,
): ICollectRemovedSnapshotsResult {
  const removedDimensions: IFieldSnapshot[] = [];
  const removedMetrics: IFieldSnapshot[] = [];

  pushRemovedFromList(
    input.columnDimensions,
    input.deleteCustomColumnId,
    'dimension',
    removedDimensions,
  );
  pushRemovedFromList(
    input.columnMetrics,
    input.deleteCustomColumnId,
    'metric',
    removedMetrics,
  );
  pushRemovedFromList(
    input.columnX ?? [],
    input.deleteCustomColumnId,
    'dimension',
    removedDimensions,
  );
  pushRemovedFromList(
    input.columnShowDimensions ?? [],
    input.deleteCustomColumnId,
    'dimension',
    removedDimensions,
  );

  return { removedDimensions, removedMetrics };
}

export interface IBuildRemovalChangeSetInput {
  removedDimensions: IFieldSnapshot[];
  removedMetrics: IFieldSnapshot[];
  nextSnapshot: IQBSnapshot;
  prevDimCount: number;
  prevMetCount: number;
  prevHasDateDimension: boolean;
}

/** 构造与 detectQBChanges 等价的删除 changeSet */
export function buildRemovalChangeSet(
  input: IBuildRemovalChangeSetInput,
): IQBChangeSet | null {
  const { removedDimensions, removedMetrics, nextSnapshot } = input;
  if (!removedDimensions.length && !removedMetrics.length) {
    return null;
  }

  const events = new Set<TQBEvent>();
  const categories = new Set<TQBCategory>();

  if (removedDimensions.length) {
    events.add('dimensionRemoved');
    categories.add('dimension');
  }
  if (removedMetrics.length) {
    events.add('metricRemoved');
    categories.add('metric');
  }

  const nextDims = snapshotList(nextSnapshot.columnDimensions, 'dimension');
  const nextMets = snapshotList(nextSnapshot.columnMetrics, 'metric');
  const hasDateDimension =
    nextSnapshot.hasDateDimension ??
    detectDateDimensionsInList(nextSnapshot.columnDimensions);

  if (input.prevDimCount !== nextDims.length) {
    events.add('dimCountChanged');
  }
  if (input.prevMetCount !== nextMets.length) {
    events.add('metricCountChanged');
  }
  if (hasDateDimension !== input.prevHasDateDimension) {
    events.add('hasDateDimChanged');
  }

  return {
    events,
    categories,
    addedDimensions: [],
    removedDimensions,
    addedMetrics: [],
    removedMetrics,
    renamed: [],
    dimCount: nextDims.length,
    metricCount: nextMets.length,
    hasDateDimension,
  };
}

function runRemovalPipelineOnLayers(
  changeSet: IQBChangeSet,
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  qb: IQBLinkageContext['qb'],
): { base?: Record<string, unknown>; draft?: Record<string, unknown> } {
  const patch = runQBLinkagePipeline(
    changeSet,
    { base, draft, qb },
    getQBLinkageHandlers(),
  );
  if (
    !Object.keys(patch.base).length &&
    !Object.keys(patch.draft).length
  ) {
    return {};
  }
  return {
    base: Object.keys(patch.base).length ? patch.base : undefined,
    draft: Object.keys(patch.draft).length ? patch.draft : undefined,
  };
}

/** 对单层配置（base 或 draft 或 save 内层）执行删除 prune */
export function pruneConfigLayerOnQBRemoval(
  layer: Record<string, unknown>,
  changeSet: IQBChangeSet,
  qb: IQBLinkageContext['qb'],
): Record<string, unknown> | undefined {
  const patch = runRemovalPipelineOnLayers(changeSet, layer, {}, qb);
  if (!patch.base) {
    return undefined;
  }
  return mergeConfigLayer({ ...layer }, patch.base);
}

/**
 * 同步 DVA `save_column_infos.chartsCfgSave[chartType]`
 */
export function pruneSaveColumnInfosOnCustomColumnDelete(
  saveColumnInfos: unknown,
  chartType: TChartType,
  changeSet: IQBChangeSet,
  qb: IQBLinkageContext['qb'],
): unknown {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object') {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as Record<string, unknown>;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, { customizeSetting?: Record<string, Record<string, unknown>> }>
    | undefined;
  const typeKey = chartType as string;
  const storageKey = ConfigChartType[chartType];
  const wrapper = chartsCfgSave?.[typeKey];
  const inner = wrapper?.customizeSetting?.[storageKey];
  if (!inner || typeof inner !== 'object') {
    return saveColumnInfos;
  }
  const pruned = pruneConfigLayerOnQBRemoval(
    { ...(inner as Record<string, unknown>) },
    changeSet,
    qb,
  );
  if (!pruned) {
    return saveColumnInfos;
  }
  return {
    ...root,
    chartsCfgSave: {
      ...chartsCfgSave,
      [typeKey]: {
        ...wrapper,
        customizeSetting: {
          ...wrapper!.customizeSetting,
          [storageKey]: pruned,
        },
      },
    },
  };
}

function patchSaveColumnInfosInner(
  saveColumnInfos: unknown,
  chartType: TChartType,
  nextInner: Record<string, unknown>,
): unknown {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object') {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as Record<string, unknown>;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, { customizeSetting?: Record<string, Record<string, unknown>> }>
    | undefined;
  const typeKey = chartType as string;
  const storageKey = ConfigChartType[chartType];
  const wrapper = chartsCfgSave?.[typeKey];
  const inner = wrapper?.customizeSetting?.[storageKey];
  if (!inner || typeof inner !== 'object') {
    return saveColumnInfos;
  }
  return {
    ...root,
    chartsCfgSave: {
      ...chartsCfgSave,
      [typeKey]: {
        ...wrapper,
        customizeSetting: {
          ...wrapper!.customizeSetting,
          [storageKey]: nextInner,
        },
      },
    },
  };
}

/**
 * A1：持久化层字段排序引用已删自定义字段时回默认
 */
export function pruneSaveColumnInfosOnDatasetFieldSortCustomDelete(
  saveColumnInfos: unknown,
  chartType: TChartType,
  deletedNames: readonly string[],
): unknown {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object' || !deletedNames.length) {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as Record<string, unknown>;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, { customizeSetting?: Record<string, Record<string, unknown>> }>
    | undefined;
  const typeKey = chartType as string;
  const storageKey = ConfigChartType[chartType];
  const wrapper = chartsCfgSave?.[typeKey];
  const inner = wrapper?.customizeSetting?.[storageKey];
  if (!inner || typeof inner !== 'object') {
    return saveColumnInfos;
  }
  const pruned = pruneDatasetFieldSortLayerIfNeeded(
    { ...(inner as Record<string, unknown>) },
    layer => shouldResetDatasetFieldSortOnCustomDelete(layer, deletedNames),
  );
  if (!pruned) {
    return saveColumnInfos;
  }
  return patchSaveColumnInfosInner(saveColumnInfos, chartType, pruned);
}

/**
 * B1：持久化层字段排序引用在最新表结构中不可解析时回默认
 */
export function pruneSaveColumnInfosOnDatasetFieldSortUnresolved(
  saveColumnInfos: unknown,
  chartType: TChartType,
  dataTables: IGetTableInfoDto[],
  customColumns: CustomColumn[],
  dataTablesSchemaReady = true,
): unknown {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object') {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as Record<string, unknown>;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, { customizeSetting?: Record<string, Record<string, unknown>> }>
    | undefined;
  const typeKey = chartType as string;
  const storageKey = ConfigChartType[chartType];
  const wrapper = chartsCfgSave?.[typeKey];
  const inner = wrapper?.customizeSetting?.[storageKey];
  if (!inner || typeof inner !== 'object') {
    return saveColumnInfos;
  }
  const pruned = pruneDatasetFieldSortLayerIfNeeded(
    { ...(inner as Record<string, unknown>) },
    layer =>
      shouldResetDatasetFieldSortOnUnresolved(
        layer,
        dataTables,
        customColumns,
        dataTablesSchemaReady,
      ),
  );
  if (!pruned) {
    return saveColumnInfos;
  }
  return patchSaveColumnInfosInner(saveColumnInfos, chartType, pruned);
}

